import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, fmtDate } from "@/lib/format";
import { CurrencyInr, ShieldCheck, DownloadSimple } from "@phosphor-icons/react";

function downloadCsv(filename, rows) {
  if (!rows.length) return;
  const cols = Object.keys(rows[0]);
  const escape = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    if (/[",\n]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
    return s;
  };
  const lines = [cols.join(",")].concat(rows.map((r) => cols.map((c) => escape(r[c])).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function RegistrationCostsReport({ dateFrom, dateTo, branchId, branchTypeId, modelCategory }) {
  const [data, setData] = useState({ rows: [], totals: { count: 0, insurance_sum: 0, registration_sum: 0, both_sum: 0 }, by_branch: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (branchId) params.branch_id = branchId;
        if (branchTypeId) params.branch_type_id = branchTypeId;
        if (modelCategory) params.model_category = modelCategory;
        const { data } = await api.get("/reports/registration-costs", { params });
        setData(data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [dateFrom, dateTo, branchId, branchTypeId, modelCategory]);

  const csvRows = useMemo(() => data.rows.map((r) => ({
    Branch: r.branch_name,
    VIN: r.vin,
    Model: r.vehicle_model,
    Customer: r.customer_name,
    Started: fmtDate(r.started_at),
    Finance: r.is_finance ? "Yes" : "No",
    "Insurance ₹": r.insurance_amount,
    "Insurance Ref": r.insurance_ref,
    "Insurance Done": fmtDate(r.insurance_done_at),
    "Registration ₹": r.registration_amount,
    "RTO Ref": r.registration_ref,
    "RTO Done": fmtDate(r.registration_done_at),
    "Total ₹": r.total,
  })), [data.rows]);

  return (
    <div className="space-y-4" data-testid="registration-costs-report">
      {/* Totals tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="border-2 border-ink p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">VINs Tracked</div>
          <div className="font-display text-3xl font-bold tabular-nums">{data.totals.count}</div>
        </div>
        <div className="border-2 border-canvas-line p-4">
          <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
            <ShieldCheck size={11} weight="bold" /> Insurance Paid
          </div>
          <div className="font-display text-2xl font-bold tabular-nums text-ink">
            {fmtMoney(data.totals.insurance_sum)}
          </div>
        </div>
        <div className="border-2 border-canvas-line p-4">
          <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
            <CurrencyInr size={11} weight="bold" /> RTO Reg. Paid
          </div>
          <div className="font-display text-2xl font-bold tabular-nums text-ink">
            {fmtMoney(data.totals.registration_sum)}
          </div>
        </div>
        <div className="border-2 border-[#00B341] p-4 bg-[#00B341]/5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Combined Total</div>
          <div className="font-display text-2xl font-bold tabular-nums text-[#00713a]">
            {fmtMoney(data.totals.both_sum)}
          </div>
        </div>
      </div>

      {/* By-branch aggregate */}
      {data.by_branch.length > 0 && (
        <div className="border-2 border-ink">
          <div className="px-4 py-2 border-b border-canvas-line font-display font-semibold">By Branch</div>
          <table className="w-full text-sm">
            <thead className="bg-canvas-panel">
              <tr>
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase text-ink-muted">Branch</th>
                <th className="text-right px-3 py-2 font-mono text-[10px] uppercase text-ink-muted">VINs</th>
                <th className="text-right px-3 py-2 font-mono text-[10px] uppercase text-ink-muted">Insurance ₹</th>
                <th className="text-right px-3 py-2 font-mono text-[10px] uppercase text-ink-muted">RTO ₹</th>
                <th className="text-right px-3 py-2 font-mono text-[10px] uppercase text-ink-muted">Total ₹</th>
              </tr>
            </thead>
            <tbody>
              {data.by_branch.map((b) => (
                <tr key={b.branch_id} className="border-t border-canvas-line">
                  <td className="px-3 py-2">{b.branch_name}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{b.count}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(b.insurance_sum)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(b.registration_sum)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums font-bold">{fmtMoney(b.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Per-VIN drilldown */}
      <div className="border-2 border-ink">
        <div className="px-4 py-2 border-b border-canvas-line flex items-center justify-between">
          <span className="font-display font-semibold">Per-VIN Detail</span>
          <button
            type="button"
            onClick={() => downloadCsv("registration-costs.csv", csvRows)}
            disabled={!csvRows.length}
            data-testid="reg-costs-csv-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-ink text-xs font-mono uppercase tracking-wider hover:bg-ink hover:text-white disabled:opacity-50"
          >
            <DownloadSimple size={11} weight="bold" /> Export CSV
          </button>
        </div>
        {loading ? (
          <div className="p-10 text-center text-ink-muted text-sm">Loading…</div>
        ) : data.rows.length === 0 ? (
          <div className="p-10 text-center text-ink-muted text-sm">
            No registration costs captured in this period yet. Open a registration → Insurance / Payment stage → enter the amount + receipt number.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-canvas-panel">
                <tr>
                  <th className="text-left px-2 py-2 font-mono text-[10px] uppercase text-ink-muted">VIN</th>
                  <th className="text-left px-2 py-2 font-mono text-[10px] uppercase text-ink-muted">Model</th>
                  <th className="text-left px-2 py-2 font-mono text-[10px] uppercase text-ink-muted">Customer</th>
                  <th className="text-left px-2 py-2 font-mono text-[10px] uppercase text-ink-muted">Branch</th>
                  <th className="text-right px-2 py-2 font-mono text-[10px] uppercase text-ink-muted">Insurance ₹</th>
                  <th className="text-left px-2 py-2 font-mono text-[10px] uppercase text-ink-muted">Policy/Ref</th>
                  <th className="text-right px-2 py-2 font-mono text-[10px] uppercase text-ink-muted">RTO ₹</th>
                  <th className="text-left px-2 py-2 font-mono text-[10px] uppercase text-ink-muted">RTO Ref</th>
                  <th className="text-right px-2 py-2 font-mono text-[10px] uppercase text-ink-muted">Total ₹</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.registration_id} className="border-t border-canvas-line">
                    <td className="px-2 py-1.5 font-mono text-[11px]">{r.vin}</td>
                    <td className="px-2 py-1.5">{r.vehicle_model}</td>
                    <td className="px-2 py-1.5">{r.customer_name}</td>
                    <td className="px-2 py-1.5 text-ink-muted">{r.branch_name}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums">{r.insurance_amount ? fmtMoney(r.insurance_amount) : "—"}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-ink-muted">{r.insurance_ref || "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums">{r.registration_amount ? fmtMoney(r.registration_amount) : "—"}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-ink-muted">{r.registration_ref || "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums font-bold">{fmtMoney(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
